import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';

import { authenticationSelector, selectPostAuthLocation } from '~/components/authentication/ducks/selectors';
import { selectEnableQRCodeLogin } from '~/components/brand/ducks';
import { isSmallSelector } from '~/components/layout/mediaquery';
import useTranslations from '~/components/translation/use-translations.ts';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import { isAndroidChrome } from '~/utils/user-agent-utils';

import { BankIDTextKeys } from './bankid-text-keys';
import BankIDWithoutForm from './bankid-without-form';
import { BANKID_MODES, BANKID_SCREEN } from './configs';
import { bankIdLoading as bankLoadingAction, clearBankID } from './ducks/actions';
import BankIDForm from './form';
import BankIDLoader from './loader/bankid-loader-new';
import MiddleScreen from './middle-screen';
import useBankID from './use-bankid';
import { clearOrderRefCookie, getUrlWithoutRef, retrieveOrderRef, setAnalyticsCookies } from './utils';

const BankID = ({
	autoStart,
	mode = BANKID_MODES.LOGIN,
	title = 'login',
	noScreen,
	gaEventCb,
	onPollingReset = () => {}
}) => {
	const [autoStartBankID, setAutoStartBankID] = useState(false);
	const [screen, setScreen] = useState(null);
	const [bankidLoading, setLoading] = useState({
		show: false,
		status: 'none'
	});
	// bankid hook for starting bankid and checking status of bankid
	const { startBankID, bankIDTransaction, cancelBankId, setPollConfig } = useBankID({
		mode
	});
	const isAuthenticated = useSelector(authenticationSelector);
	const { orderRef } = useSelector(state => state.bankId, shallowEqual);
	const smaller = useSelector(isSmallSelector);
	const enableQRCodeLogin = useSelector(selectEnableQRCodeLogin);
	const isQrEnabled = enableQRCodeLogin;
	const dispatch = useDispatch();
	// hydrate translations, used in redux store for error
	// eslint-disable-next-line no-unused-vars
	const { translations } = useTranslations(BankIDTextKeys);
	const location = useLocation();
	const router = useRouter();
	const postAuthLocation = useSelector(selectPostAuthLocation);
	// check if user initiated login
	const hasLoginReferrer = location?.state?.loginReferrer;
	// select bankid screen
	useEffect(() => {
		// TODO: clean this up, need to keep it simple, after launch of new registration flow
		// for swish in smaller, we dont need any ui, just autostart.
		if (noScreen) {
			return;
		}
		// form is shown on mobile only for register, because of t&c checkbox
		if (smaller && mode !== BANKID_MODES.REGISTER) {
			if (mode === BANKID_MODES.PLAY_NOW) {
				setScreen(BANKID_SCREEN.MIDDLE_SCREEN);
			} else {
				setScreen(BANKID_SCREEN.BASIC);
			}
		} else if (isQrEnabled) {
			setScreen(BANKID_SCREEN.QR);
		} else {
			setScreen(BANKID_SCREEN.FORM);
		}
	}, [mode, smaller, noScreen, isQrEnabled]);

	// enable autoStart
	useEffect(() => {
		const orderRefCached = retrieveOrderRef();
		// only needs to execute once
		if (isAuthenticated || orderRef || orderRefCached || bankIDTransaction.status !== 'none') {
			return;
		}
		// check if user initiated bankid request
		const isUserInitiated = noScreen || hasLoginReferrer;
		// if its not a user initiated login, we cannot auto start on chrome
		const chromeBlockedAutoStart = isAndroidChrome() && !isUserInitiated;
		const canAutoStart = autoStart && !chromeBlockedAutoStart;
		// enable autostart on mobile & if its not trustly by default
		if (canAutoStart) {
			setAutoStartBankID(true);
		}
	}, [autoStart, orderRef, isAuthenticated, hasLoginReferrer, noScreen, bankIDTransaction.status]);

	// start BankID
	const initiateBankID = useCallback(
		(payload = {}) => {
			startBankID(payload);
		},
		[startBankID]
	);

	// hook for autostarting bankid
	useEffect(() => {
		if (autoStartBankID) {
			initiateBankID();
		}
	}, [autoStartBankID, initiateBankID]);

	// use mode to set cookies for gtm events
	useEffect(() => {
		setAnalyticsCookies(mode);
	}, [mode]);

	// hook for bankid failure
	useEffect(() => {
		if (bankIDTransaction.status === 'failure') {
			onPollingReset();
		}
	}, [bankIDTransaction.status, onPollingReset]);

	// hook for bankid loader
	useEffect(() => {
		if (bankIDTransaction.status === 'start') {
			dispatch(bankLoadingAction());
			setLoading(loading => ({
				...loading,
				...{ show: true, status: 'start' }
			}));
		} else if (bankIDTransaction.status === 'restart') {
			dispatch(bankLoadingAction());
			setLoading(loading => ({
				...loading,
				...{ show: true, status: 'pending' }
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bankIDTransaction.status]);

	// clear cached order refs used for mobile ios
	const clearOrderRefCache = useCallback(() => {
		const newSearch = getUrlWithoutRef();
		const state = { from: postAuthLocation };
		if (newSearch) {
			// TODO (navigation) - use search params or redux for state
			router.push(`${location.pathname}?${newSearch}`, { state });
		}
		clearOrderRefCookie();
	}, [postAuthLocation, router, location.pathname]);

	// for ios chrome, it opens a new tab
	// cache the order ref in a cookie
	// initiate polling with this order ref
	useEffect(() => {
		const orderRefCached = retrieveOrderRef();
		// only needs to execute once
		if (orderRef || bankIDTransaction.status !== 'none') {
			return;
		}
		if (orderRefCached) {
			const payload = {
				orderRef: orderRefCached
			};
			startBankID(payload);
			clearOrderRefCache();
		}
	}, [orderRef, dispatch, clearOrderRefCache, startBankID, bankIDTransaction.status]);

	const showForm = useCallback(() => setScreen(BANKID_SCREEN.QR), [setScreen]);

	// close sidebar
	const closeSidebar = useCloseCurrentContext();

	useEffect(() => {
		if (bankIDTransaction.status === 'failure') {
			setLoading(loading => ({
				...loading,
				...{ show: false, status: 'none' }
			}));
		}
	}, [bankIDTransaction.status]);

	// on cancel bankid
	const onCancel = useCallback(
		async (e, closeOnCancel) => {
			dispatch(clearBankID());
			setLoading(loading => ({
				...loading,
				...{ show: true, status: 'canceling' }
			}));
			await cancelBankId(orderRef);
			setLoading(loading => ({
				...loading,
				...{ show: false, status: 'none' }
			}));
			if (smaller && closeOnCancel) {
				closeSidebar();
			}
		},
		[dispatch, smaller, closeSidebar, cancelBankId, orderRef]
	);

	const getBankIDScreen = () => {
		switch (screen) {
			case BANKID_SCREEN.FORM:
			case BANKID_SCREEN.QR:
				return (
					<BankIDForm
						mode={mode}
						onSubmit={initiateBankID}
						title={title}
						gaEventCb={gaEventCb}
						loading={bankidLoading.show}
						onCancel={onCancel}
						setPollConfig={setPollConfig}
						transaction={bankIDTransaction}
						setScreen={setScreen}
						screen={screen}
						isQrEnabled={isQrEnabled}
					/>
				);
			case BANKID_SCREEN.BASIC:
				return (
					<BankIDWithoutForm
						onClick={() => initiateBankID()}
						title={title}
						mode={mode}
						loading={bankidLoading.show}
					/>
				);
			case BANKID_SCREEN.MIDDLE_SCREEN:
				return (
					<MiddleScreen
						bankIDCb={initiateBankID}
						bankIDOtherCb={showForm}
						title={title}
						cancelCb={onCancel}
						gaEventCb={gaEventCb}
						bankIdLoading={bankidLoading.show}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div>
			{bankidLoading.show && screen !== BANKID_SCREEN.QR && (
				<BankIDLoader
					onCancel={onCancel}
					mode={mode}
					isWithinModal={mode === BANKID_MODES.SWISH && !smaller}
					status={bankidLoading.status}
				/>
			)}
			{getBankIDScreen()}
		</div>
	);
};

export default BankID;
